import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generateAssistCellsByAI } from '../api/ai';
import { useSettings } from '../contexts/SettingsContext';
import { useUI } from '../contexts/UIContext';

const Sidebar = ({ activeBlock, updateCellPriority, updateCell, chartData }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [draggedCellId, setDraggedCellId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const settings = useSettings();
  const { toast } = useUI();

  if (!activeBlock) return null;

  const centerCell = activeBlock.cells.find(c => c.is_center);
  const peripheryCells = activeBlock.cells.filter(c => !c.is_center).sort((a, b) => a.priority_index - b.priority_index);

  const handleAIassist = async () => {
    setIsGenerating(true);
    try {
      const results = await generateAssistCellsByAI(chartData, activeBlock.id, settings);
      if (results.length === 0) {
        toast('全てのマスが埋まっています。', 'info');
        return;
      }
      results.forEach(res => {
        const cell = peripheryCells.find(c => c.priority_index === res.priority);
        if (cell) updateCell(activeBlock.id, cell.id, res.content);
      });
    } catch (err) {
      console.error(err);
      toast(err.message || 'AIアシスト中にエラーが発生しました', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDragStart = (event, cellId) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(cellId));
    setDraggedCellId(cellId);
  };

  const handleDrop = (event, targetCell) => {
    event.preventDefault();
    const sourceId = Number(event.dataTransfer.getData('text/plain')) || draggedCellId;
    if (sourceId && sourceId !== targetCell.id) {
      updateCellPriority(activeBlock.id, sourceId, targetCell.priority_index);
    }
    setDraggedCellId(null);
    setDropTargetId(null);
  };

  return (
    <div className="h-full flex flex-col overflow-y-auto">
      <div className="p-4 border-b border-white/70 dark:border-white/10">
        <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiSettings} className="text-primary-600 dark:text-primary-400" />
          順位入れ替え & AI
        </h3>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-medium">ドラッグ&ドロップまたはセレクトボックスで配置変更</p>
      </div>

      <div className="p-4 flex-1">
        <div className="mb-6 rounded-2xl backdrop-blur-xl bg-primary-600/10 dark:bg-primary-500/10 p-3.5 border border-primary-600/15 dark:border-primary-500/20 text-center">
          <span className="text-xs font-bold text-primary-700 dark:text-primary-400 mb-1 block uppercase tracking-wide">中心テーマ</span>
          <p className="text-sm font-bold text-gray-900 dark:text-white">{centerCell?.content || '（未入力）'}</p>
        </div>

        <div className="space-y-2">
          {peripheryCells.map((cell) => (
            <div
              key={cell.id}
              onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; setDropTargetId(cell.id); }}
              onDragLeave={() => setDropTargetId((current) => current === cell.id ? null : current)}
              onDrop={(event) => handleDrop(event, cell)}
              className={`flex items-center gap-2 rounded-xl backdrop-blur-xl bg-white/60 dark:bg-white/5 p-2 border shadow-sm focus-within:border-primary-400 focus-within:ring-1 focus-within:ring-primary-400 transition-all ${draggedCellId === cell.id ? 'opacity-45' : ''} ${dropTargetId === cell.id && draggedCellId !== cell.id ? 'border-primary-500 ring-2 ring-primary-400/40' : 'border-white/70 dark:border-white/10'}`}
            >
              <button
                type="button"
                draggable
                onDragStart={(event) => handleDragStart(event, cell.id)}
                onDragEnd={() => { setDraggedCellId(null); setDropTargetId(null); }}
                className="cursor-grab touch-none rounded-lg p-1 text-gray-400 hover:bg-primary-500/10 hover:text-primary-600 active:cursor-grabbing dark:text-gray-500 dark:hover:text-primary-300"
                aria-label={`「${cell.content || `優先度 ${cell.priority_index}`}」をドラッグして並べ替え`}
                title="ドラッグして順位を入れ替え"
              >
                <SafeIcon icon={FiIcons.FiMove} className="text-base" />
              </button>
              <select
                value={cell.priority_index}
                onChange={(e) => updateCellPriority(activeBlock.id, cell.id, parseInt(e.target.value, 10))}
                className="bg-white/80 dark:bg-gray-800/80 border border-gray-300/60 dark:border-white/10 rounded-lg text-xs p-1.5 outline-none text-gray-800 dark:text-gray-100 font-mono shadow-sm"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map(num => <option key={num} value={num}>{num}</option>)}
              </select>
              <input
                type="text"
                value={cell.content || ''}
                onChange={(e) => updateCell(activeBlock.id, cell.id, e.target.value)}
                placeholder={`優先度 ${cell.priority_index} の要素`}
                className="flex-1 text-sm bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 font-medium"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 border-t border-white/70 dark:border-white/10">
        <button onClick={handleAIassist} disabled={isGenerating} className="w-full py-3 px-4 glass-btn-primary flex items-center justify-center gap-2 disabled:opacity-50">
          {isGenerating ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> : <SafeIcon icon={FiIcons.FiZap} />}
          {isGenerating ? 'AIが生成中...' : '空きマスをAIで埋める'}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;